/**
 * Custom hook for accessing environment context.
 */
import { useContext } from 'react';
import { EnvironmentContext } from '../context/EnvironmentContext';
import type { EnvironmentContextType } from '../types/environment';

/**
 * Hook to access environment context.
 *
 * @throws Error if used outside of EnvironmentProvider
 * @returns Environment context value
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { environment, setEnvironment, toggleEnvironment } = useEnvironment();
 *
 *   return (
 *     <div>
 *       <p>Current environment: {environment}</p>
 *       <button onClick={toggleEnvironment}>Toggle Environment</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useEnvironment(): EnvironmentContextType {
  const context = useContext(EnvironmentContext);

  if (context === undefined) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }

  return context;
}
